import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { assertUnreachable } from '@ftr/contracts/shared/assertUnreachable'
import { takeUntil } from 'rxjs'
import { StopPropagationDirective } from '../../directives'
import { ScreenSize, ScreenSizeService, isMobileScreenSize } from '../../services'
import { DestroySubscribers, splitPascalCase } from '../../util'
import { BadgeColors, BadgeComponent } from '../badge'
import { ButtonComponent, ButtonDisplayType, ButtonSize } from '../button'
import { Icon, IconColor, IconComponent } from '../icon'

export interface FloatingTabIcon {
  icon: Icon
  iconColor?: IconColor
  buttonTitle?: string
  isButton?: boolean
}

export interface FloatingTab<TValue> {
  label: string
  mobileLabel?: string
  headerLabel?: string
  footerLabel?: string
  title?: string
  value: TValue
  ariaLabel?: string
  // Show a badge with a number on this tab
  count?: number

  beforeLabelIcon?: FloatingTabIcon
  afterLabelIcon?: FloatingTabIcon
}

export enum FloatingTabsColorSet {
  Default = 'default',
  Primary = 'primary',
  Secondary = 'secondary',
}

@Component({
  selector: 'ftr-floating-tabs',
  templateUrl: './floating-tabs.component.html',
  styleUrls: ['./floating-tabs.component.css'],
  standalone: true,
  imports: [CommonModule, IconComponent, BadgeComponent, ButtonComponent, StopPropagationDirective],
})
export class FloatingTabsComponent<TValue> extends DestroySubscribers implements OnInit {
  @Input() tabs: FloatingTab<TValue>[]
  @Input() colorSet: FloatingTabsColorSet = FloatingTabsColorSet.Default
  @Input() selectedValue?: TValue
  @Input() margins: boolean = true
  @Input() verticalDisplay: boolean = false
  @Input() noWrap: boolean = false
  @Input() boldAllLabels: boolean = false

  /**
   * Emits the tab value when a new tab is selected i.e. will not fire on every tab click
   */
  @Output() onTabSelected = new EventEmitter<TValue>()

  /**
   * Emits the tab value whenever a tab is clicked
   */
  @Output() onTabClick = new EventEmitter<TValue>()

  /**
   * Emits the tab value whenever the icon before the label is clicked
   */
  @Output() onBeforeLabelIconClick = new EventEmitter<TValue>()

  /**
   * Emits the tab value whenever the icon after the label is clicked
   */
  @Output() onAfterLabelIconClick = new EventEmitter<TValue>()

  private currentScreenSize: ScreenSize
  readonly buttonSize = ButtonSize
  readonly buttonDisplayType = ButtonDisplayType

  constructor(private readonly screenSizeService: ScreenSizeService) {
    super()
  }

  ngOnInit(): void {
    this.screenSizeService.size.pipe(takeUntil(this.finalize)).subscribe(s => (this.currentScreenSize = s))
  }

  handleTabClick(tab: FloatingTab<TValue>): void {
    this.onTabClick.emit(tab.value)

    this.selectTab(tab)
  }

  handleBeforeLabelIconClick(tab: FloatingTab<TValue>): void {
    this.onBeforeLabelIconClick.emit(tab.value)
  }

  handleAfterLabelIconClick(tab: FloatingTab<TValue>): void {
    this.onAfterLabelIconClick.emit(tab.value)
  }

  selectTab(tab: FloatingTab<TValue>): void {
    let shouldEmit = false

    if (tab.value !== this.selectedValue) {
      shouldEmit = true
    }

    this.updateSelectedValue(tab)

    if (shouldEmit) {
      this.onTabSelected.emit(this.selectedValue)
    }
  }

  getLabel(mobileLabel: string | undefined, desktopLabel: string): string {
    return isMobileScreenSize(this.currentScreenSize) && mobileLabel ? mobileLabel : desktopLabel
  }

  getBadgeColor(tab: TValue): BadgeColors {
    if (this.selectedValue === tab) {
      switch (this.colorSet) {
        case FloatingTabsColorSet.Default:
          return BadgeColors.GreyLight
        case FloatingTabsColorSet.Primary:
          return BadgeColors.PrimaryLight
        case FloatingTabsColorSet.Secondary:
          return BadgeColors.Aqua
        default:
          assertUnreachable(this.colorSet)
      }
    }
    return BadgeColors.Grey20
  }

  private updateSelectedValue(tab: FloatingTab<TValue>): void {
    this.selectedValue = tab.value
  }
}

/**
 * Converts a string enum to an array of floating tabs.
 * Only sets the label and value, and is unaware of anything else.
 */
export function stringEnumToFloatingTabs<T>(stringEnum: {}): FloatingTab<T>[] {
  return Object.entries(stringEnum).map(([key, value]) => ({
    label: splitPascalCase(key),
    value: value as T,
  }))
}
